import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { dataQualityApi } from "../../services/dataQualityApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { NotFoundState } from "../../components/NotFoundState";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function DataQualityFindingDetailPage() {
  const { findingId } = useParams();
  const { t } = useLocalization();
  const { canViewDataQuality, canManageDataQuality } = usePermissions();
  const [finding, setFinding] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [owner, setOwner] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    dataQualityApi.getFinding(findingId).then((r) => {
      setFinding(r.data);
      setOwner(r.data.owner || "");
      setDueDate(r.data.due_date || "");
      setNotFound(false);
    }).catch((err) => {
      if (err.response?.status === 404) setNotFound(true);
    });
  }, [findingId]);

  useEffect(() => { if (canViewDataQuality) load(); }, [canViewDataQuality, load]);

  if (!canViewDataQuality) return <p className="text-sm text-gray-500" data-testid="data-quality-access-denied">{t("common.accessDenied")}</p>;
  if (notFound) return <NotFoundState testId="dq-finding-not-found" backTo="/management/data-quality" />;
  if (!finding) return <p className="text-sm text-gray-500" data-testid="dq-finding-loading">{t("common.loading")}</p>;

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); toast.success(t("mgmtHealthCommittee.updated")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const activeStatuses = ["OPEN", "ASSIGNED", "IN_PROGRESS", "WAITING_FOR_EVIDENCE", "REOPENED"];
  const isActive = activeStatuses.includes(finding.status);
  const canReopen = ["RESOLVED", "ACCEPTED_RISK", "FALSE_POSITIVE"].includes(finding.status);

  const sourceLink = finding.employee_id
    ? `/management/employees/${finding.employee_id}`
    : (finding.period_id ? `/management/payroll/periods/${finding.period_id}` : null);

  return (
    <div className="max-w-4xl space-y-5" data-testid="dq-finding-detail-page">
      <PageHelpBanner pageKey="dq-finding-detail" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtDataQuality.detailHelp")}</p>
      </PageHelpBanner>
      <Link to="/management/data-quality" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="dq-back-link">&larr; {t("mgmtDataQuality.backToList")}</Link>

      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-xl font-bold text-gray-900" data-testid="dq-finding-title">{finding.title}</h2>
          <div className="flex gap-2">
            <StatusBadge status={finding.severity} />
            <StatusBadge status={finding.status} />
          </div>
        </div>
        <p className="text-sm text-gray-600" data-testid="dq-finding-description">{finding.description}</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-gray-500 pt-2 border-t border-gray-100">
          <div><span className="block text-gray-500">{t("mgmtDataQuality.module")}</span>{finding.module}</div>
          <div><span className="block text-gray-500">{t("mgmtDataQuality.findingType")}</span>{finding.finding_type}</div>
          <div><span className="block text-gray-500">{t("mgmtDataQuality.ruleVersion")}</span>{finding.detection_rule_version}</div>
          <div><span className="block text-gray-500">{t("mgmtDataQuality.storeScope")}</span>{finding.store_scope || "-"}</div>
          <div><span className="block text-gray-500">{t("mgmtDataQuality.firstDetected")}</span>{finding.first_detected_at}</div>
          <div><span className="block text-gray-500">{t("mgmtDataQuality.lastDetected")}</span>{finding.last_detected_at}</div>
          <div><span className="block text-gray-500">{t("mgmtHealthCommittee.colOwner")}</span>{finding.owner || t("mgmtHealthCommittee.unassigned")}</div>
          <div><span className="block text-gray-500">{t("mgmtActionCenter.colDue")}</span>{finding.due_date || "-"}</div>
        </div>
        {sourceLink && (
          <Link data-testid="dq-open-source-record-link" to={sourceLink} className="inline-block text-xs font-semibold text-blue-600 hover:underline">{t("mgmtDataQuality.openSourceRecordArrow")}</Link>
        )}
      </div>

      {finding.resolution_note && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800" data-testid="dq-resolution-note">
          <strong>{t("mgmtDataQuality.resolution")}:</strong> {finding.resolution_note} {finding.resolved_by && `(${t("mgmtCorrectiveAction.addedBy")} ${finding.resolved_by})`}
        </div>
      )}
      {finding.false_positive_reason && (
        <div className="bg-gray-100 border border-gray-300 rounded-xl p-4 text-sm text-gray-700" data-testid="dq-false-positive-note">
          <strong>{t("mgmtDataQuality.falsePositive")}:</strong> {finding.false_positive_reason} {finding.false_positive_by && `(${t("mgmtCorrectiveAction.addedBy")} ${finding.false_positive_by})`}
        </div>
      )}
      {finding.risk_accepted_reason && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm text-indigo-800" data-testid="dq-risk-accepted-note">
          <strong>{t("mgmtDataQuality.riskAccepted")}:</strong> {finding.risk_accepted_reason} ({t("mgmtDataQuality.expires")} {finding.risk_accepted_expires_at}, {t("mgmtCorrectiveAction.addedBy")} {finding.risk_accepted_by})
        </div>
      )}

      {canManageDataQuality && isActive && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h3 className="text-sm font-semibold text-gray-700">{t("mgmtDataQuality.actions")}</h3>

          <div className="flex gap-2 flex-wrap items-end">
            <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtHealthCommittee.colOwner")}</label>
              <input data-testid="dq-owner-input" value={owner} onChange={(e) => setOwner(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" placeholder="owner@example.com" />
            </div>
            <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtActionCenter.colDue")}</label>
              <input data-testid="dq-due-date-input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
            </div>
            <button data-testid="dq-assign-btn" disabled={busy || !owner} onClick={() => run(() => dataQualityApi.assign(finding.id, owner, dueDate || null, note || null))}
              className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{t("mgmtDataQuality.assign")}</button>
            {["OPEN", "ASSIGNED", "REOPENED", "WAITING_FOR_EVIDENCE"].includes(finding.status) && (
              <button data-testid="dq-start-btn" disabled={busy} onClick={() => run(() => dataQualityApi.start(finding.id, note || null))}
                className="px-4 py-2 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("mgmtDataQuality.startWork")}</button>
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">{t("mgmtDataQuality.note")}</label>
            <textarea data-testid="dq-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder={t("mgmtDataQuality.notePlaceholder")} />
          </div>

          <div className="flex gap-2 flex-wrap">
            <button data-testid="dq-resolve-btn" disabled={busy || !note} onClick={() => run(() => dataQualityApi.resolve(finding.id, note))}
              className="px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">{t("mgmtDataQuality.resolve")}</button>
            <button data-testid="dq-false-positive-btn" disabled={busy || !note} onClick={() => run(() => dataQualityApi.falsePositive(finding.id, note))}
              className="px-4 py-2 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50 disabled:opacity-50">{t("mgmtDataQuality.markFalsePositive")}</button>
            <div className="flex gap-2 items-center">
              <input data-testid="dq-risk-expires-input" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" placeholder={t("mgmtDataQuality.riskExpires")} />
              <button data-testid="dq-accept-risk-btn" disabled={busy || !note || !expiresAt} onClick={() => run(() => dataQualityApi.acceptRisk(finding.id, note, expiresAt))}
                className="px-4 py-2 rounded-lg text-xs font-semibold border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">{t("mgmtDataQuality.acceptRisk")}</button>
            </div>
          </div>
          {finding.severity === "CRITICAL" && <p className="text-xs text-gray-500">{t("mgmtDataQuality.criticalRiskNote")}</p>}
        </div>
      )}

      {canManageDataQuality && canReopen && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">{t("mgmtDataQuality.reopen")}</h3>
          <textarea data-testid="dq-reopen-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder={t("mgmtDataQuality.reasonForReopening")} />
          <button data-testid="dq-reopen-btn" disabled={busy || !reason} onClick={() => run(() => dataQualityApi.reopen(finding.id, reason))}
            className="px-4 py-2 rounded-lg text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50">{t("mgmtDataQuality.reopenFinding")}</button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("mgmtHealthCommittee.auditTimeline")}</h3>
        <div className="space-y-2">
          {(finding.audit_history || []).slice().reverse().map((h, idx) => (
            <div key={idx} className="text-xs border-l-2 border-gray-200 pl-3 py-1" data-testid={`dq-audit-entry-${idx}`}>
              <span className="font-semibold text-gray-700">{h.action}</span> <span className="text-gray-500">{t("mgmtHealthCommittee.by")} {h.actor} {t("mgmtHealthCommittee.at")} {h.at}</span>
              {h.notes && <p className="text-gray-500 mt-0.5">{h.notes}</p>}
            </div>
          ))}
          {(!finding.audit_history || finding.audit_history.length === 0) && <p className="text-xs text-gray-500" data-testid="no-dq-audit-history">{t("mgmtHealthCommittee.noAuditHistory")}</p>}
        </div>
      </div>
    </div>
  );
}
